import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Route, RouteProps } from "react-router-dom";
import { iRootState } from "../store";
import styled, { css } from "styled-components";
import qs from "query-string";
import HeaderComponent from "./HeaderComponent";
import { setLanguage, bridgeAuthentication } from "../service/UserService";
// @ts-ignore
import { getCurUser, CWBLoadingCrest } from "cwb-react";
import { setTrackingData, setWebSessionKey } from "../helpers";
import * as Sentry from "@sentry/react";
import { HEADER_HEIGHT } from "../constants";
import CanonicalLinkPortal from "./CanonicalLinkPortal";

interface Props extends RouteProps, StateProps, DispatchProps {
  path?: string;
}

const BridgeAuthOptionalRoute: React.FC<Props> = ({
  setAuthenticatedUser,
  authenticatedUser,
  component: Component,
  path,
  ...rest
}) => {
  const o = qs.parse(rest.location.search);
  const hasKey = !!(o && o.k);

  // Only block rendering with a loading state when a session key is present
  const [isLoading, setIsLoading] = useState<boolean>(hasKey);

  useEffect(() => {
    setTrackingData(o.src as string, rest.location.search);
  }, []);

  useEffect(() => {
    if (!hasKey) return;

    (async function attemptAuth() {
      try {
        if (!authenticatedUser || authenticatedUser.id.userTypeId !== 8) {
          // Store the session key in localStorage before any other requests
          setWebSessionKey(o.k as string);

          // Perform bridge authentication so auth state is ready before page renders
          await bridgeAuthentication(o.k as string);

          const user = getCurUser();
          if (user) {
            await setLanguage();
            setAuthenticatedUser(user);
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        setIsLoading(false);
      }
    })();
  }, []);

  const params = qs.parse(rest.location.search);
  const showHeader = !params.hs;

  return (
    <>
      {showHeader && <HeaderComponent />}
      <Wrapper showHeader={showHeader}>
        {isLoading ? (
          <CWBLoadingCrest customLogoSrc="/images/icon-twb-logo-grey.svg" />
        ) : (
          <Route
            {...rest}
            render={(routeProps) => (
              <>
                {path && <CanonicalLinkPortal href={path} />}
                {Component ? (
                  <Component {...routeProps} />
                ) : rest.render ? (
                  rest.render(routeProps)
                ) : null}
              </>
            )}
          />
        )}
      </Wrapper>
    </>
  );
};

const Wrapper: any = styled.div`
  display: flex;
  padding-top: ${HEADER_HEIGHT}px;

  ${(props: any) =>
    !props.showHeader &&
    css`
      padding-top: 0;
    `}

  @media all and (max-width: 520px) {
    flex-direction: column;
  }

  @media all and (max-width: 768px) {
    padding-top: 0;
  }
`;

interface StateProps {
  authenticatedUser: any;
}

const mapStateToProps = (state: iRootState) => ({
  authenticatedUser: state.appModel.authenticatedUser,
});

interface DispatchProps {
  setAuthenticatedUser: (user: any) => any;
}

const mapDispatchToProps = (dispatch: any) => ({
  setAuthenticatedUser: dispatch.appModel.setAuthenticatedUser,
});

export default connect(mapStateToProps, mapDispatchToProps)(BridgeAuthOptionalRoute);
